import React from 'react';
import { Button, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useUpdateAdminMunicipalitySection,
  useDeleteAdminMunicipalitySection,
  useImportAdminMunicipalitySection,
  useRunAdminMunicipalitySectionLambda,
  useRunAdminMunicipalitySectionFullImport
} from 'api/hooks/use-admin-municipality-sections';
import { useAdminSystems } from 'api/hooks/use-admin-systems';

const MunicipalitySectionRow = ({ municipalityId, municipalitySection }) => {
  const { data: systems } = useAdminSystems();

  const { mutate: updateMs, isLoading: updating } = useUpdateAdminMunicipalitySection({
    municipalityId,
    options: {
      onError: (err) => toast.error(err?.response?.data?.message || 'Update failed')
    }
  });

  const { mutate: deleteMs, isLoading: deleting } = useDeleteAdminMunicipalitySection({
    municipalityId,
    options: {
      onSuccess: () => toast.success('Module removed'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Remove failed')
    }
  });

  const { mutate: importMs, isLoading: importing } = useImportAdminMunicipalitySection({
    municipalityId,
    options: {
      onSuccess: (data) => toast.success(data?.message || 'Import queued'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Import failed')
    }
  });

  const { mutate: runLambda, isLoading: runningLambda } = useRunAdminMunicipalitySectionLambda({
    municipalityId,
    options: {
      onSuccess: (data) => toast.success(data?.message || 'Lambda queued'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to queue lambda')
    }
  });

  const { mutate: runFullImport, isLoading: runningFullImport } = useRunAdminMunicipalitySectionFullImport({
    municipalityId,
    options: {
      onSuccess: (data) => toast.success(data?.message || 'Full import queued'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to queue full import')
    }
  });

  const toggle = (field) => (e) => {
    updateMs({ id: municipalitySection.id, params: { [field]: e.target.checked } });
  };

  const handleSystemChange = (e) => {
    const value = e.target.value
    // system_id is a UUID — keep it as a string. Coercing to Number would
    // produce NaN, which serializes to null and clears the system.
    updateMs({ id: municipalitySection.id, params: { system_id: value === '' ? null : value } });
  };

  const { section } = municipalitySection;
  const busy = runningLambda || importing || runningFullImport || deleting;

  return (
    <tr>
      <td>
        <div>{section.name}</div>
        <small className="text-muted">
          <code>{section.abbreviation}</code>
          {section.folder && <> · folder: <code>{section.folder}</code></>}
        </small>
      </td>
      <td>
        <Form.Select
          size="sm"
          value={municipalitySection.system?.id ?? ''}
          disabled={updating || !systems}
          onChange={handleSystemChange}
          aria-label="System"
        >
          <option value="">— None —</option>
          {systems?.map((sys) => (
            <option key={sys.id} value={sys.id}>{sys.name}</option>
          ))}
        </Form.Select>
      </td>
      <td className="text-center">
        <Form.Check
          type="checkbox"
          aria-label="Has workflow"
          checked={!!municipalitySection.has_workflow}
          disabled={updating}
          onChange={toggle('has_workflow')}
        />
      </td>
      <td className="text-center">
        <Form.Check
          type="checkbox"
          aria-label="Has allocations"
          checked={!!municipalitySection.has_allocations}
          disabled={updating}
          onChange={toggle('has_allocations')}
        />
      </td>
      <td className="text-center">
        <Form.Check
          type="checkbox"
          aria-label="Silence stale-file alerts"
          title="Skip this module's folder in the nightly S3 freshness check"
          checked={!!municipalitySection.freshness_check_silenced}
          disabled={updating}
          onChange={toggle('freshness_check_silenced')}
        />
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            if (window.confirm(`Run the ${section.name} lambda? This invokes AWS Lambda and may take several minutes.`)) {
              runLambda(municipalitySection.id)
            }
          }}
          disabled={busy}
          className="me-2"
        >
          {runningLambda ? <Spinner animation="border" size="sm" /> : 'Run Lambda'}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => importMs(municipalitySection.id)}
          disabled={busy}
          className="me-2"
        >
          {importing ? <Spinner animation="border" size="sm" /> : 'Import'}
        </Button>
        {section.abbreviation !== 'SOD' && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`full-import-tooltip-${municipalitySection.id}`}>
                Re-imports every row for this module, bypassing the terminal state of transactions —
                closed POs, cleared checks and recorded workflow actions are overwritten instead of skipped.
              </Tooltip>
            }
          >
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (window.confirm(`Run a full import of ${section.name}? This re-imports every row, including transactions already in a terminal state, and may take a while.`)) {
                  runFullImport(municipalitySection.id)
                }
              }}
              disabled={busy}
              className="me-2"
            >
              {runningFullImport ? <Spinner animation="border" size="sm" /> : 'Full Import'}
            </Button>
          </OverlayTrigger>
        )}
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => {
            if (window.confirm(`Remove ${section.name} from this municipality? This cannot be undone.`)) {
              deleteMs(municipalitySection.id)
            }
          }}
          disabled={busy}
        >
          {deleting ? <Spinner animation="border" size="sm" /> : 'Remove'}
        </Button>
      </td>
    </tr>
  );
};

export default MunicipalitySectionRow;
